// caiet ACCS-16, CF-18 — a dashboard definition in Romanian, with what it says
// in the other two interface languages (docs/shape-ops-dashboard-localization.md
// §2.2). The analytics service validates the list, the bff grants the Grafana
// dashboards it names, and the reading page resolves it for its language.

// The record's own title, description and Grafana uid are the Romanian ones.
export const DASHBOARD_TRANSLATION_LOCALES = ['ru', 'en'] as const;

export type DashboardTranslationLocale = (typeof DASHBOARD_TRANSLATION_LOCALES)[number];

// An empty field falls back to the Romanian one.
export interface DashboardTranslation {
  locale: string;
  title: string;
  description: string;
  grafanaUid: string;
}

export function isDashboardTranslationLocale(value: string): value is DashboardTranslationLocale {
  return (DASHBOARD_TRANSLATION_LOCALES as readonly string[]).includes(value);
}

interface Translatable {
  title: string;
  description: string;
  grafanaUid?: string;
  translations?: readonly Partial<DashboardTranslation>[];
}

// The definition as a viewer reading `locale` sees it.
export function localizeDashboard<T extends Translatable>(dashboard: T, locale: string): T {
  const translation = dashboard.translations?.find((entry) => entry.locale === locale);
  if (!translation) return dashboard;
  return {
    ...dashboard,
    title: translation.title || dashboard.title,
    description: translation.description || dashboard.description,
    ...(dashboard.grafanaUid === undefined
      ? {}
      : { grafanaUid: translation.grafanaUid || dashboard.grafanaUid }),
  };
}

// Every Grafana dashboard one definition embeds, whichever language reads it.
export function dashboardGrafanaUids(dashboard: {
  grafanaUid?: string;
  translations?: readonly Partial<DashboardTranslation>[];
}): string[] {
  const uids = [dashboard.grafanaUid, ...(dashboard.translations ?? []).map((t) => t.grafanaUid)];
  return [...new Set(uids.map((uid) => uid?.trim() ?? '').filter((uid) => uid.length > 0))];
}
